import cn from 'classnames';
import { Children, cloneElement, useEffect, useRef, useState } from 'react';

import * as styles from './styles.css';

export interface IAccordionRootProps {
  startIndex?: number;
  onClick?(): void;
  isTwoColumn?: boolean;
}

const ANIMATION_DURATION = 250;

export const AccordionRoot: React.FC<React.PropsWithChildren<IAccordionRootProps>> = ({
  startIndex,
  onClick,
  isTwoColumn,
  children,
}) => {
  const accordionHtmlItems = useRef<Array<HTMLDivElement | null>>([]);
  const [activeItem, setActiveItem] = useState<null | number>(typeof startIndex == 'number' ? startIndex : null);

  /**
   * Если при открытии аккордиона заголовок скрылся за пределами экрана,
   * делаем автоскролл к заголовку
   */
  useEffect(() => {
    if (accordionHtmlItems.current && activeItem !== null) {
      const activeElement = accordionHtmlItems.current[activeItem] as HTMLDivElement;
      const topPotision = activeElement.getBoundingClientRect().top;
      const isHideTop = topPotision < 0;

      if (isHideTop) {
        setTimeout(() => {
          window.scrollTo({
            top: activeElement.offsetTop,
            left: 0,
            behavior: 'smooth',
          });
        }, ANIMATION_DURATION);
      }
    }
  }, [activeItem]);

  const onHandleClick = (index: number) => {
    const nextIndex = index === activeItem ? null : index;

    setActiveItem(nextIndex);

    if (onClick) {
      onClick();
    }
  };

  function generateTwoColumn() {
    const childCounts = Children.count(children);
    const one: React.ReactElement[] = [];
    const two: React.ReactElement[] = [];

    Children.map(children, (child, index) => {
      const isLessMiddle = index < Math.ceil(childCounts / 2);
      const arr = isLessMiddle ? one : two;

      arr.push(
        <div key={index} data-testid="accordion-item" ref={ref => (accordionHtmlItems.current[index] = ref)}>
          {cloneElement(child as React.ReactElement, {
            isFirst: index === 0 || index === Math.ceil(childCounts / 2),
            isActive: index === activeItem,
            onClick: () => onHandleClick(index),
          })}
        </div>,
      );
    });

    return (
      <>
        <div>{one}</div>
        <div>{two}</div>
      </>
    );
  }

  return (
    <div className={cn({ [styles['root']]: isTwoColumn })}>
      {isTwoColumn
        ? generateTwoColumn()
        : Children.map(children, (child, index) => {
            return (
              <div data-testid="accordion-item" ref={ref => (accordionHtmlItems.current[index] = ref)}>
                {cloneElement(child as React.ReactElement, {
                  isFirst: index === 0,
                  isActive: index === activeItem,
                  onClick: () => onHandleClick(index),
                })}
              </div>
            );
          })}
    </div>
  );
};

AccordionRoot.displayName = 'AccordionRoot';
